import axios, { CancelToken } from 'axios';

export interface UploadPreparedMediaOptions {
	onProgress?: (progress: number) => void;
	onServerTranscoding?: () => void;
}

export const getApiBaseUrl = (): string =>
	process.env.REACT_APP_JOC_API ||
	process.env.NEXT_PUBLIC_API_URL ||
	(typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000');

export const getS3BaseUrl = (): string =>
	process.env.REACT_APP_S3_API || 'https://develop-user-logos.s3.eu-central-1.amazonaws.com';

export const getFileUrl = (fileName: string): string => `${getS3BaseUrl().replace(/\/+$/, '')}/${fileName}`;

export const getErrorMessage = (error: unknown): string => {
	if (axios.isCancel(error)) {
		return 'Upload was cancelled.';
	}
	if (axios.isAxiosError(error)) {
		const status = error.response?.status;
		const responseData = error.response?.data as { message?: string | string[]; error?: string } | undefined;
		const details = Array.isArray(responseData?.message)
			? responseData?.message.join(', ')
			: responseData?.message || responseData?.error || error.message;

		return `Server Error (${status || 'Network'}): ${details}`;
	}
	if (error instanceof Error) {
		return error.message;
	}
	return 'Media upload failed due to an unexpected error.';
};

const EXTENSION_TO_MIME: Record<string, string> = {
	hevc: 'video/mp4',
	h265: 'video/mp4',
	mp4: 'video/mp4',
	mov: 'video/quicktime',
	mkv: 'video/x-matroska',
	webm: 'video/webm',
	avi: 'video/x-msvideo',
	jpg: 'image/jpeg',
	jpeg: 'image/jpeg',
	png: 'image/png',
	webp: 'image/webp',
	gif: 'image/gif',
	avif: 'image/avif',
	heic: 'image/heic',
	heif: 'image/heic',
	pdf: 'application/pdf',
	doc: 'application/msword',
	docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	xls: 'application/vnd.ms-excel',
	xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	csv: 'text/csv',
	txt: 'text/plain',
};

export const resolveNormalizedFile = (file: File): File => {
	if (file.type && file.type !== 'application/octet-stream') {
		return file;
	}

	const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
	const resolvedMime = EXTENSION_TO_MIME[extension];

	if (resolvedMime) {
		return new File([file], file.name, { type: resolvedMime, lastModified: file.lastModified });
	}

	return file;
};

export const isVideoFormat = (file: File): boolean =>
	file.type.startsWith('video/') || /\.(hevc|h265|mp4|mov|mkv|webm|avi)$/i.test(file.name);

export const uploadPreparedMedia = async (
	file: File,
	needsServerTranscode: boolean,
	optionsOrProgress?: ((progress: number) => void) | UploadPreparedMediaOptions
): Promise<string> => {
	const normalizedFile = resolveNormalizedFile(file);
	const isVideo = isVideoFormat(normalizedFile);
	const effectiveNeedsServerTranscode = needsServerTranscode || isVideo;

	const onProgress = typeof optionsOrProgress === 'function' ? optionsOrProgress : optionsOrProgress?.onProgress;
	const onServerTranscoding =
		typeof optionsOrProgress === 'object' && optionsOrProgress !== null
			? optionsOrProgress.onServerTranscoding
			: undefined;

	const body = new FormData();
	body.append('file', normalizedFile, normalizedFile.name);
	body.append('needsServerTranscode', String(effectiveNeedsServerTranscode));

	const baseUrl = getApiBaseUrl();
	const url = `${baseUrl.replace(/\/+$/, '')}/files/upload-stream`;

	try {
		const response = await axios.post<{ fileName: string }>(url, body, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
			onUploadProgress: event => {
				if (!event.total) return;
				const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
				onProgress?.(percent);
				if (percent >= 100 && needsServerTranscode) {
					onServerTranscoding?.();
				}
			},
		});

		if (!response.data?.fileName) {
			throw new Error('Media upload did not return a file name.');
		}

		return response.data.fileName;
	} catch (error) {
		throw new Error(getErrorMessage(error));
	}
};

export interface UploadAttachmentOptions {
	onProgress?: (progress: number) => void;
	onProcessing?: () => void;
	cancelToken?: CancelToken;
}

export const uploadAttachment = async (
	file: File,
	optionsOrProgress?: ((progress: number) => void) | UploadAttachmentOptions
): Promise<string> => {
	const onProgress = typeof optionsOrProgress === 'function' ? optionsOrProgress : optionsOrProgress?.onProgress;
	const onProcessing =
		typeof optionsOrProgress === 'object' && optionsOrProgress !== null
			? optionsOrProgress.onProcessing
			: undefined;
	const cancelToken =
		typeof optionsOrProgress === 'object' && optionsOrProgress !== null ? optionsOrProgress.cancelToken : undefined;

	const normalizedFile = resolveNormalizedFile(file);
	const body = new FormData();
	body.append('file', normalizedFile, normalizedFile.name);

	const baseUrl = getApiBaseUrl();
	const url = `${baseUrl.replace(/\/+$/, '')}/files/upload-attachment`;

	try {
		const response = await axios.post<{ fileName: string }>(url, body, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
			cancelToken,
			onUploadProgress: event => {
				if (!event.total) return;
				const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
				if (percent < 100) {
					onProgress?.(percent);
				} else {
					onProgress?.(100);
					onProcessing?.();
				}
			},
		});

		if (!response.data?.fileName) {
			throw new Error('File upload did not return a file name.');
		}

		return response.data.fileName;
	} catch (error) {
		throw new Error(getErrorMessage(error));
	}
};
